import React, { useRef } from 'react';
import clsx from 'clsx';
import { MdCheckCircle, MdError } from 'react-icons/md';
import { IOffer } from '@models/winnerSelection/ITableDemand';
import { ITableKOItem, ITableKOSettings } from '@models/winnerSelection/ITableKO';
import OfferKOCritDetails, { IOfferKOCritDetailsHandle } from '../OfferKOCritDetails';

interface IProps {
    crit: ITableKOItem;
    offer: IOffer;
    settings: ITableKOSettings | undefined;
    demandId: string;
    tableKey: string;
    onReload: () => void;
}

/**
 * Ячейка KO-таблицы:
 * - result=1 → «Допущен» (success-tint);
 * - result=0 → «Не допущен» (error-tint);
 * - result=null → «—» (neutral).
 *
 * Кнопка «Подробнее» — OfferKOCritDetails-модалка для редактирования решения.
 *
 * Memo: пропсы стабильны между reload'ами, отсекает ре-рендер при изменении
 * parent'а не из-за данных таблицы.
 */
const OfferCellKOInner: React.FC<IProps> = ({ crit, offer, settings, demandId, tableKey, onReload }) => {
    const offerCrit = crit.offers[offer.key];
    const result = offerCrit?.result;
    const isAllowed = result === 1;
    const isDenied = result === 0;
    const isEmpty = result == null;

    const cellRef = useRef<HTMLDivElement>(null);
    const detailsRef = useRef<IOfferKOCritDetailsHandle>(null);

    /** Клик по всей ячейке открывает детали. Guard по DOM-contains отсекает
     *  всплытие кликов портированной в body модалки обратно по React-дереву. */
    const handleCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (cellRef.current?.contains(e.target as Node)) {
            detailsRef.current?.open();
        }
    };

    const handleCellKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Guard по DOM-contains — как в handleCellClick: клавиши портированной в
        // body модалки всплывают по React-дереву обратно сюда, иначе preventDefault
        // съедает пробел в textarea деталей.
        if (!cellRef.current?.contains(e.target as Node)) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            detailsRef.current?.open();
        }
    };

    return (
        <div
            ref={cellRef}
            className={clsx('ws-ko-cell', {
                'ws-ko-cell--allowed': isAllowed,
                'ws-ko-cell--denied': isDenied,
                'ws-ko-cell--empty': isEmpty,
            })}
            role="button"
            tabIndex={0}
            aria-label={`Подробнее: критерий «${crit.critName}»`}
            onClick={handleCellClick}
            onKeyDown={handleCellKeyDown}
        >
            <div className="ws-ko-cell__value">
                {isAllowed && (
                    <>
                        <span className="ws-ko-cell__icon">
                            <MdCheckCircle size="20px" />
                        </span>
                        Допущен
                    </>
                )}
                {isDenied && (
                    <>
                        <span className="ws-ko-cell__icon">
                            <MdError size="20px" />
                        </span>
                        Не допущен
                    </>
                )}
                {isEmpty && <span>—</span>}
            </div>

            <OfferKOCritDetails
                ref={detailsRef}
                tableKey={tableKey}
                isEdit={offerCrit?.allowEdit ?? false}
                disabledHint={settings?.offerCritDisabledHint}
                editButtonName={settings?.editOfferCritButtonName ?? 'Подробнее'}
                offerId={offer.key}
                demandId={demandId}
                value={offerCrit}
                rowData={crit}
                onSubmit={onReload}
            />
        </div>
    );
};

export const OfferCellKO = React.memo(OfferCellKOInner);
